"use client"

import { useRef } from "react"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"

import {
  isHeroVideo,
  MAX_HERO_INTERVAL_MS,
  type HeroAnimationEffect,
  type HeroMedia,
} from "@workspace/model"
import { cn } from "@workspace/ui/lib/utils"

gsap.registerPlugin(useGSAP)

/** Longest crossfade/slide, in seconds; shorter intervals scale it down. */
const MAX_TRANSITION_S = 1.2
/** Ken Burns end scale — leaves ~6% per side of headroom for the pan. */
const KEN_BURNS_SCALE = 1.12
const KEN_BURNS_PAN_PERCENT = 3
/** A video slide advances when its clip ends, or after this at the latest. */
const MAX_VIDEO_HOLD_S = MAX_HERO_INTERVAL_MS / 1000

/**
 * A stack of tenant hero slides — photos and/or muted videos — cycled by GSAP
 * (`StoreConfig.heroAnimation`, resolved by `resolveHero`). Used for both the
 * hero background and the decorative card; each instance runs its own
 * independent loop.
 *
 * The slides are server-rendered with only the first one visible, so the hero
 * is complete without JS and with `prefers-reduced-motion` — motion only starts
 * under `gsap.matchMedia("(prefers-reduced-motion: no-preference)")`. Hidden
 * slides sit at `autoAlpha: 0` (`visibility: hidden`), so only the active one
 * is exposed to assistive tech.
 *
 * Videos never carry `autoplay`: they show their poster until JS starts them,
 * and stay on the poster under reduced motion or a Save-Data connection. Only
 * the active video plays, and only while the hero is on screen and the tab is
 * visible. In a cycling slot a photo holds for `intervalMs` and a video until
 * its clip ends (capped at 30s); a lone video, or one with effect `none`,
 * simply loops.
 *
 * Plain `<img>` (arbitrary per-tenant CDNs, not registered with next/image) —
 * mirrors the menu/featured item images.
 */
export function HeroSlideshow({
  images,
  effect,
  intervalMs,
  decorative = false,
  className,
}: {
  images: HeroMedia[]
  effect: HeroAnimationEffect
  intervalMs: number
  /** Background use: hide the whole stack from assistive tech, drop alts. */
  decorative?: boolean
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const cycles = images.length > 1 && effect !== "none"
  const playsVideo = images[0] !== undefined && isHeroVideo(images[0])

  useGSAP(
    () => {
      if (!(cycles || playsVideo) || !ref.current) return
      const root = ref.current
      const slides = gsap.utils.toArray<HTMLElement>("[data-hero-slide]", root)
      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const hold = intervalMs / 1000
        const transition = Math.min(MAX_TRANSITION_S, hold * 0.3)
        // Slides enter from the end side of the reading direction.
        const direction = getComputedStyle(root).direction === "rtl" ? -1 : 1
        const saveData =
          (navigator as Navigator & { connection?: { saveData?: boolean } })
            .connection?.saveData === true
        const videoAt = (index: number) => {
          const slide = slides[index]
          return !saveData && slide instanceof HTMLVideoElement
            ? slide
            : undefined
        }

        let current = 0
        let inView = true
        let pending: gsap.core.Tween | undefined
        // Detaches the active video's `ended` listener on every advance.
        let listeners = new AbortController()

        const canPlay = () => inView && !document.hidden

        const playCurrent = () => {
          const video = videoAt(current)
          if (!video) return
          if (!canPlay()) {
            video.pause()
            return
          }
          // React doesn't reliably reflect `muted` onto the element, and
          // browsers refuse to autoplay unmuted video.
          video.muted = true
          video.play().catch((error: unknown) => {
            // AbortError = our own pause() interrupted it (scrolled away, tab
            // hidden, advanced) — not a refusal.
            if (error instanceof DOMException && error.name === "AbortError")
              return
            // Autoplay refused (e.g. power saving): stay on the poster and
            // hold like a photo so the slideshow doesn't stall.
            if (video !== videoAt(current) || !cycles) return
            pending?.kill()
            pending = gsap.delayedCall(hold, advance)
            pending.paused(!canPlay())
          })
        }

        const syncPlayback = () => {
          playCurrent()
          pending?.paused(!canPlay())
        }

        const schedule = (index: number) => {
          const video = videoAt(index)
          if (video) {
            video.currentTime = 0
            if (cycles) {
              video.addEventListener("ended", advance, {
                once: true,
                signal: listeners.signal,
              })
            }
            playCurrent()
          }
          if (!cycles) return
          pending = gsap.delayedCall(video ? MAX_VIDEO_HOLD_S : hold, advance)
          pending.paused(!canPlay())
          // Warm up the next clip while this slide is on screen.
          const nextVideo = videoAt((index + 1) % slides.length)
          if (nextVideo) nextVideo.preload = "auto"
        }

        const kenBurns = (slide: HTMLElement, index: number) => {
          // Videos already move; zooming them too reads as jitter.
          if (slide instanceof HTMLVideoElement) return
          gsap.fromTo(
            slide,
            { scale: 1, xPercent: 0 },
            {
              scale: KEN_BURNS_SCALE,
              xPercent:
                index % 2 === 0
                  ? KEN_BURNS_PAN_PERCENT
                  : -KEN_BURNS_PAN_PERCENT,
              duration: hold + transition,
              ease: "none",
            }
          )
        }

        function advance() {
          listeners.abort()
          listeners = new AbortController()
          pending?.kill()

          const next = (current + 1) % slides.length
          const from = slides[current]!
          const to = slides[next]!
          const hideFrom = () => {
            gsap.set(from, { autoAlpha: 0, xPercent: 0 })
            if (from instanceof HTMLVideoElement) from.pause()
          }
          gsap.set(slides, { zIndex: 0 })
          gsap.set(from, { zIndex: 1 })
          gsap.set(to, { zIndex: 2 })

          if (effect === "slide") {
            gsap.fromTo(
              to,
              { autoAlpha: 1, xPercent: 100 * direction },
              { xPercent: 0, duration: transition, ease: "power2.inOut" }
            )
            gsap.to(from, {
              xPercent: -100 * direction,
              duration: transition,
              ease: "power2.inOut",
              onComplete: hideFrom,
            })
          } else {
            // fade / kenBurns: fade the incoming slide in OVER the outgoing
            // one, then hide the outgoing — the section never shows through.
            if (effect === "kenBurns") kenBurns(to, next)
            gsap.fromTo(
              to,
              { autoAlpha: 0 },
              {
                autoAlpha: 1,
                duration: transition,
                ease: "power1.inOut",
                onComplete: hideFrom,
              }
            )
          }

          current = next
          schedule(current)
        }

        const observer = new IntersectionObserver(([entry]) => {
          inView = entry?.isIntersecting ?? true
          syncPlayback()
        })
        observer.observe(root)
        document.addEventListener("visibilitychange", syncPlayback)

        if (effect === "kenBurns" && cycles) kenBurns(slides[0]!, 0)
        schedule(0)

        // Tweens created by later `advance` calls aren't recorded by this
        // context, so put the stack back to its server-rendered state by hand
        // (first slide visible, the rest hidden, videos on their posters) —
        // e.g. when the diner turns on reduced motion mid-loop, or the hero
        // unmounts.
        return () => {
          observer.disconnect()
          document.removeEventListener("visibilitychange", syncPlayback)
          listeners.abort()
          pending?.kill()
          gsap.killTweensOf(slides)
          for (const slide of slides) {
            if (slide instanceof HTMLVideoElement) slide.pause()
          }
          gsap.set(slides, { clearProps: "transform,zIndex" })
          gsap.set(slides[0]!, { autoAlpha: 1 })
          // A lone looping video has no other slides to hide.
          if (slides.length > 1) gsap.set(slides.slice(1), { autoAlpha: 0 })
        }
      })
    },
    {
      scope: ref,
      dependencies: [
        cycles,
        playsVideo,
        effect,
        intervalMs,
        images
          .map((image) => `${image.type ?? "image"}:${image.url}`)
          .join("\n"),
      ],
      revertOnUpdate: true,
    }
  )

  if (images.length === 0) return null

  return (
    <div
      ref={ref}
      aria-hidden={decorative || undefined}
      className={cn("relative overflow-hidden", className)}
    >
      {images.map((image, index) => {
        const slideClassName = cn(
          "absolute inset-0 size-full object-cover",
          cycles && "will-change-transform"
        )
        const hidden =
          index === 0
            ? undefined
            : { opacity: 0, visibility: "hidden" as const }

        return isHeroVideo(image) ? (
          <video
            key={`${index}:${image.url}`}
            data-hero-slide
            src={image.url}
            poster={image.posterUrl}
            muted
            playsInline
            loop={!cycles}
            // With a poster nothing needs fetching until JS plays the clip;
            // without one, fetch metadata so the first frame can show.
            preload={image.posterUrl ? "none" : "metadata"}
            aria-label={decorative ? undefined : image.alt || undefined}
            className={slideClassName}
            style={hidden}
          />
        ) : (
          <img
            key={`${index}:${image.url}`}
            data-hero-slide
            src={image.url}
            alt={decorative ? "" : (image.alt ?? "")}
            // The first photo is the LCP candidate; the rest load behind it.
            fetchPriority={index === 0 ? "high" : "low"}
            decoding="async"
            className={slideClassName}
            style={hidden}
          />
        )
      })}
    </div>
  )
}
